import { type ApiResponse } from "apisauce"

import { request } from "../../../services/api"
import type { BasePagination } from "../../../types/api.ts"
import type {
  Notification,
  NotificationSettingsItem
} from "../types/notification.types"

const BASE_URL = "/notifications"

export const NOTIFICATION_PATHS = {
  getNotifications: (accessToken: string) =>
    `${import.meta.env.VITE_APP_BASE_API_WS_ROUTE}/notifications/?token=${accessToken}`,
  getNotificationSettings: `${BASE_URL}/notification_settings/`,
  updateNotificationSettings: `${BASE_URL}/notification_settings/`,
  pushSubscriptions: `${BASE_URL}/push-subscriptions/`,
  // The REST list — separate from the WS feed above, which is the only
  // thing the notification bell/popover otherwise uses (see
  // docs/kb/flows/realtime-notifications.md). Reused here for a cheap
  // per-type unread count, not for the feed itself.
  list: `${BASE_URL}/notifications/`,
  read: (id: number) => `${BASE_URL}/notifications/${id}/read/`
}

export const getNotificationSettings = (): Promise<ApiResponse<any>> => {
  return request.api.get(NOTIFICATION_PATHS.getNotificationSettings)
}

export const updateNotificationSettings = (
  data: Partial<NotificationSettingsItem>[]
): Promise<ApiResponse<any>> => {
  return request.api.patch(NOTIFICATION_PATHS.updateNotificationSettings, data)
}

export const registerPushSubscription = (
  subscription: PushSubscriptionJSON
): Promise<ApiResponse<any>> => {
  return request.api.post(NOTIFICATION_PATHS.pushSubscriptions, subscription)
}

export const unregisterPushSubscription = (
  endpoint: string
): Promise<ApiResponse<any>> => {
  // apisauce's second `delete()` argument is a query-string `params` object,
  // not a body — the backend reads `request.data`, so the payload has to go
  // through axios's own `data` config instead.
  return request.api.delete(
    NOTIFICATION_PATHS.pushSubscriptions,
    {},
    { data: { endpoint } }
  )
}

// GET /notifications/notifications/?notification_name=<n>&unread=true&page_size=100
// — the envelope's `count` is the badge total; `results` (each carrying its
// own `data` — project_id/name, event_id/start_at) is what the Home card
// names the actual new item from, and what gets marked read on page visit.
// One call serves all three needs, so there's nothing to keep in sync.
//
// `notification_name` (not `notification_type`) is deliberate: `type` is the
// coarse Notification.Type category ("project", "calendar", ...), which
// groups distinct events together (PROJECT_CREATED and PROJECT_DELETED are
// both "project") — filtering on it would count every project event, not
// just new ones. `notification_name` is the precise numeric NotificationType
// id (11 = PROJECT_CREATED). ezbuild_back's NotificationFilter needs a
// matching `notification_name` filter field for this to work — see
// docs/kb/decisions/2026-09-12-notification-badges-phase-1.md.
export const getUnreadNotificationsByType = (notificationType: number) => {
  return request.api.get<BasePagination<Notification>>(
    NOTIFICATION_PATHS.list,
    {
      notification_name: notificationType,
      unread: true,
      page_size: 100
    }
  )
}

export const markNotificationRead = (id: number) => {
  return request.api.post(NOTIFICATION_PATHS.read(id))
}
